import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { RootState } from "typedefs";
import Portal from "app/components/portal";
import useModalFocus from "app/utils/hooks/useModalFocus";
import { isAcademyOnboardingCoach } from "app/utils/subscription";
import { localGet, localSet } from "app/utils/LocalStorage";
import { trackChecklistCompleted } from "app/utils/analytics-helpers";
import useAcademySetupItems from "app/utils/hooks/useAcademySetupItems";
// 720x513 (1x) webp with a real transparent background — the same device
// composition the Welcome hero uses, which is what Figma 112:18796 renders.
import hero_image from "app/images/academy-wizard-hero.webp";
import { allComplete } from "../items";
import {
  completionSourceCounts,
  readCompletionSources,
} from "../completionSources";
import { SetupCompleteState, nextSetupCompleteStep } from "./state";
import styles from "./styles.module.scss";

/**
 * Per-user, because the celebration fires once ever and a shared browser
 * would otherwise burn one coach's modal on another's account.
 */
const storageKey = (userId: string | number) =>
  `academy-setup-complete-modal-${userId}`;

/**
 * The designed "You're all set up" celebration (Figma 112:18794). Mounted in
 * SiteWrapper so it fires wherever the coach happens to be when the last
 * checklist item lands, matching the design's Calendar Settings backdrop.
 */
const SetupCompleteModal = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { self } = useSelector((state: RootState) => state.user, shallowEqual);
  const { items, loaded, settled } = useAcademySetupItems();
  const [open, setOpen] = useState(false);
  const ctaRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  useModalFocus(open, cardRef, ctaRef);

  const eligible = isAcademyOnboardingCoach(self);
  // Booleans, not the `items` array — `withCompletion` rebuilds it every
  // render, which would re-run the effect on each pass.
  // `settled`: an optimistic visit/skip whose PUT is still in flight must not
  // fire the one-time celebration — a failure would revert the row after it.
  const complete = loaded && settled && allComplete(items);
  // The tally reads the items of the render that completed, through a ref
  // so the array stays out of the effect's dependencies (see above).
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    if (!eligible || !loaded || !self) {
      return;
    }

    const key = storageKey(self.id);
    const stored = localGet(key) as SetupCompleteState | undefined;
    const { store, celebrate } = nextSetupCompleteStep(stored, complete);

    if (store) {
      localSet(key, store);
    }
    if (celebrate) {
      trackChecklistCompleted(
        completionSourceCounts(itemsRef.current, readCompletionSources(self.id)),
      );
      setOpen(true);
    }
  }, [eligible, loaded, complete, self]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  if (!open) {
    return null;
  }

  const title = t("You're all set up");

  const onGoHome = () => {
    setOpen(false);
    history.push("/");
  };

  return (
    <Portal>
      <div
        className={styles.overlay}
        onClick={() => setOpen(false)}
        data-testid="academy-setup-complete-modal"
      >
        <div
          ref={cardRef}
          className={styles.card}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          onClick={(event) => event.stopPropagation()}
        >
          <div className={styles.image_panel}>
            <img className={styles.hero} src={hero_image} alt="" />
          </div>
          <div className={styles.texts}>
            <h2 className={styles.title}>{title}</h2>
            <p className={styles.body}>
              {t(
                "Your academy is ready to accept bookings and manage athletes. Time to put it to work.",
              )}
            </p>
          </div>
          <button
            ref={ctaRef}
            type="button"
            className={styles.cta}
            onClick={onGoHome}
          >
            {t("Go to Home")}
          </button>
        </div>
      </div>
    </Portal>
  );
};

export default SetupCompleteModal;
